import { cn } from '@forest/shared'
import { onCleanup, onMount, type JSXElement } from 'solid-js'

/**
 * The Shop full-screen outcome scene. It centers processing/reward content above
 * both dialog layers and makes the covered app unreachable while mounted.
 *
 * The scene owns modality positively: its full-viewport root blocks pointers,
 * and it marks both local siblings and shared-dialog portal siblings `inert`
 * so keyboard and assistive tech cannot reach what it covers. Layered scenes
 * compose: an upper scene inerts a lower scene's root and restores only what
 * it changed on exit. The processing overlay manages its own focus; reward outcomes expose only
 * their visible actions above the inert page.
 */
export function OverlayScene(props: {
  children: JSXElement
  /** Extra root classes (e.g. the reveal's opaque radial background). */
  class?: string
  /** Keeps panels off the screen edge on 320px viewports. */
  padded?: boolean
}) {
  let root: HTMLDivElement | undefined

  onMount(() => {
    const parent = root?.parentElement
    if (!root || !parent) return
    const scene = root
    // Sweep both local siblings and body-level portal siblings (shared
    // dialogs live there). Never inert the ancestor that contains this
    // scene. Fellow scenes stacked above are skipped: in one render pass
    // Solid inserts every sibling before any onMount flushes, so without
    // the DOM-order check a lower scene would deaden an upper scene it can't
    // see is on top. A sibling already inert belongs to a lower scene (or
    // another owner) and is skipped so exit releases only our attributes.
    const candidates = new Set([
      ...Array.from(parent.children),
      ...Array.from(document.body.children),
    ])
    const changed = Array.from(candidates).filter(
      (element) =>
        element !== scene &&
        !element.contains(scene) &&
        !element.hasAttribute('inert') &&
        !(
          element.hasAttribute('data-overlay-scene') &&
          scene.compareDocumentPosition(element) &
            Node.DOCUMENT_POSITION_FOLLOWING
        ),
    )
    for (const element of changed) element.setAttribute('inert', '')
    onCleanup(() => {
      for (const element of changed) element.removeAttribute('inert')
    })
  })

  return (
    // One computed class expression on purpose: a reactive `class` mixed
    // with `classList` lets a class swap (the reveal's radial) wipe the
    // classList-managed keys — structural classes live in this one call.
    <div
      ref={(element) => {
        root = element
      }}
      data-overlay-scene=""
      class={cn(
        'fixed inset-0 z-[80] grid min-w-[320px] place-items-center',
        props.padded && 'px-6',
        props.class,
      )}
    >
      {props.children}
    </div>
  )
}
